import { useMeetingState } from '@daily-co/daily-react'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'store/hooks/useSelector'
import { useCallIsActive } from '../../useCallIsActive'
import { useCallTimedOut } from '../../useCallTimedOut'
import { useTransitionState } from './useTransitionState'
import { useRecordAction } from './useRecordAction'
import type { ActionHistoryEntry, VisitLifecycleAction } from './useTrackActionHistory'
import { useProviderConnected } from '../../useProviderConnected'
import { usePatientConnected } from '../../usePatientConnected'
import { type UseDailyDisconnectMethodsReturn } from '../../useDailyDisconnectMethods'

// States are mutually exclusive - only one can be true at a time
export type VisitLifecycleState =
  | 'IDLE'
  | 'PROVIDER_CONNECTING'
  | 'PROVIDER_CONNECTED_AND_WAITING_FOR_PATIENT_CONNECTION'
  | 'PROVIDER_WAS_CONNECTED_BUT_PATIENT_CONNECTION_FAILED'
  | 'CALL_IS_ACTIVE'
  | 'CONNECTED_CALL_ENDED'

type Input = { lastJoinAttemptDidConnectWithPatient: boolean } & Pick<UseDailyDisconnectMethodsReturn, 'endVisitCall'>

export function useCurrentState(input: Input) {
  const { lastJoinAttemptDidConnectWithPatient, endVisitCall } = input

  const [currentState, setCurrentState] = useState<VisitLifecycleState>('IDLE')
  const [stateHistory, setStateHistory] = useState<VisitLifecycleState[]>(['IDLE'])
  const [actionHistory, setActionHistory] = useState<ActionHistoryEntry[]>([])

  const meetingState = useMeetingState()

  const { providerInitiatingVisit, isVideoDeclined } = useSelector((state) => {
    const wr = state.waitingRoomReducer
    return {
      providerInitiatingVisit: wr.providerInitiatingVisit,
      isVideoDeclined: wr.isVideoDeclined,
    }
  })

  const { callIsActive } = useCallIsActive()
  const { callTimedOut } = useCallTimedOut({ endVisitCall })
  // Track if we've had a service interruption (so we only record SERVICE_RESUMED after an interruption)
  const hadServiceInterruptionRef = useRef(false)

  const transitionState = useTransitionState({ setCurrentState, setStateHistory, setActionHistory })

  useEffect(() => {
    if (currentState === 'IDLE') {
      recordedActionsRef.current.clear()
      hadServiceInterruptionRef.current = false // Reset service interruption tracking
    }
  }, [currentState])

  const { providerConnected } = useProviderConnected()
  const { patientConnected } = usePatientConnected()

  // Determine current state based on conditions (mutually exclusive)
  useEffect(() => {
    let newState: VisitLifecycleState = 'IDLE'

    // Priority order matters - check highest priority first
    if (callIsActive) {
      newState = 'CALL_IS_ACTIVE'
    } else if (callTimedOut || isVideoDeclined) {
      // Provider was connected but call is not active
      // Check if patient connection failed (timeout or declined)
      newState = 'PROVIDER_WAS_CONNECTED_BUT_PATIENT_CONNECTION_FAILED'
    } else if (
      meetingState === 'joining-meeting' ||
      meetingState === 'loading' ||
      providerInitiatingVisit ||
      // there is a single tick after provider initiates connection (switching state to PROVIDER_CONNECTING) where the meeting
      // info has come in but providerInitiatingVisit has been toggled back off before the connection actually starts. In this
      // case, assume we are still connecting to daily and continue as PROVIDER_CONNECTING
      (currentState === 'PROVIDER_CONNECTING' && meetingState === 'new')
    ) {
      newState = 'PROVIDER_CONNECTING'
    } else if (lastJoinAttemptDidConnectWithPatient) {
      newState = 'CONNECTED_CALL_ENDED'
    } else if (providerConnected) {
      newState = 'PROVIDER_CONNECTED_AND_WAITING_FOR_PATIENT_CONNECTION'
    }
    if (newState !== currentState) {
      transitionState(newState)
    }
  }, [
    callIsActive,
    meetingState,
    lastJoinAttemptDidConnectWithPatient,
    providerConnected,
    providerInitiatingVisit,
    callTimedOut,
    isVideoDeclined,
    currentState,
    transitionState,
    patientConnected,
  ])

  // Track recorded actions to prevent duplicates (only record each action once)
  const recordedActionsRef = useRef<Set<VisitLifecycleAction>>(new Set())

  const recordAction = useRecordAction({ recordedActionsRef, setActionHistory })

  return { currentState, stateHistory, actionHistory, recordAction, callTimedOut }
}
